// Adapter between core-api wire shapes and the dashboard's opportunity shape.
// Handles renames, salary formatting, and surfaces engagement counters for KPI cards.

export const CORE_API_URL = process.env.VUE_APP_CORE_API_URL || 'http://localhost:8090'

// Single source of truth for the status transition actions shown across the
// opportunity UI (row gear menu, bulk bar, detail header). `variant` matches
// each status' badge colour (see statusVariant): active=green, rejected=red,
// in_review=yellow, incomplete=blue, closed=grey. Order is the display order;
// Close stays last.
export const STATUS_ACTIONS = [
  { value: 'active', label: 'Approve', variant: 'success' },
  { value: 'rejected', label: 'Reject', variant: 'danger' },
  { value: 'in_review', label: 'In Review', variant: 'warning' },
  { value: 'incomplete', label: 'Incomplete', variant: 'info' },
  { value: 'closed', label: 'Close', variant: 'secondary' },
]

// Nullish fallback. Webpack 4 here does not parse `??`.
function nullish(value, fallback) {
  return value === null || value === undefined ? fallback : value
}

const TYPE_LABELS = {
  'full-time': 'Full-Time',
  'part-time': 'Part-Time',
  hourly: 'Hourly',
  contract: 'Contract',
  internship: 'Internship',
}

function formatLocation(loc) {
  if (!loc) return ''
  const cityState = [loc.city, loc.state].filter(Boolean).join(', ')
  const tail = loc.remote ? ' (Remote)' : loc.raw ? ` (${loc.raw})` : ''
  return cityState + tail
}

function formatSalaryRange(salary) {
  if (!salary) return null
  const { min, max, period } = salary
  if (min == null && max == null) return null
  const fmt = (n) => (n < 1000 ? `$${n}` : `$${Math.round(n / 1000)}k`)
  const range =
    min != null && max != null
      ? `${fmt(min)}-${fmt(max)}`
      : min != null
      ? `${fmt(min)}+`
      : `Up to ${fmt(max)}`
  const suffix = period === 'hour' ? ' / hr' : period === 'month' ? ' / mo' : ' / yr'
  return range + suffix
}

// core-api opportunity -> dashboard opportunity shape.
export function coreApiOpportunityToDashboard(raw) {
  if (!raw || typeof raw !== 'object') return null
  const submitter = (raw.source && raw.source.submitter) || {}
  return {
    _id: raw._id,
    communityId: raw.communityId || raw.community || null,
    title: raw.title || '',
    company: raw.company || '',
    location: formatLocation(raw.location),
    locationStruct: raw.location || null,
    jobType: TYPE_LABELS[raw.type] || raw.type || '',
    type: raw.type || null,
    status: raw.status || 'in_review',
    submittedDate: raw.submissionDate || raw.createdAt || null,
    expirationDate: raw.expirationDate || null,
    deadline: raw.deadline || null,
    description: raw.description || '',
    applicationUrl: raw.applicationURL || '',
    industry: raw.industry || [],
    skills: raw.skills || [],
    salary: raw.salary
      ? {
          start: nullish(raw.salary.min, null),
          end: nullish(raw.salary.max, null),
          currency: raw.salary.currency || 'USD',
          period: raw.salary.period || 'year',
          disclosed: nullish(raw.salary.disclosed, null),
          display: formatSalaryRange(raw.salary),
        }
      : null,
    requiredExperienceYears: nullish(raw.requiredExperienceYears, null),
    requiredEducation: raw.requiredEducation || null,
    source: {
      channel: (raw.source && raw.source.channel) || 'program-page',
      firstName: submitter.firstName || null,
      lastName: submitter.lastName || null,
      email: submitter.email || null,
    },
    engagement: {
      // `emailed` is bumped when a match transitions to SENT (SendGrid confirms real send
      // via webhook `processed` event after core-api PR #36). Legacy `delivered` field is
      // read only for backward-compat with pre-PR#36 docs; new docs use `emailed`.
      emailed: (raw.engagement && raw.engagement.emailed) || 0,
      delivered: (raw.engagement && raw.engagement.delivered) || 0,
      // SendGrid webhook events (auto-tracked)
      opened: (raw.engagement && raw.engagement.opened) || 0,
      clicked: (raw.engagement && raw.engagement.clicked) || 0,
      unsubscribe: (raw.engagement && raw.engagement.unsubscribe) || 0,
      // Active endpoints (populated once alum triggers the corresponding action)
      notInterested: (raw.engagement && raw.engagement.notInterested) || 0,
      referral: (raw.engagement && raw.engagement.referral) || 0,
      applied: (raw.engagement && raw.engagement.applied) || 0,
      secured: (raw.engagement && raw.engagement.secured) || 0,
      feedback: (raw.engagement && raw.engagement.feedback) || 0,
    },
    // KPI cards in the list read these top-level fields.
    openCount: (raw.engagement && raw.engagement.opened) || 0,
    clickCount: (raw.engagement && raw.engagement.clicked) || 0,
    statusHistory: raw.statusHistory || [],
    activityLog: raw.activityLog || [],
    createdAt: raw.createdAt || null,
    updatedAt: raw.updatedAt || null,
  }
}

// Admin-facing labels shared by the report export. `active` reads as "Approved"
// to match the Approve/Reject actions used across the opportunity UI.
const REPORT_STATUS_LABELS = {
  in_review: 'In Review',
  active: 'Approved',
  closed: 'Closed',
  rejected: 'Rejected',
  incomplete: 'Incomplete',
  quarantined: 'Quarantined',
}

const REPORT_SOURCE_LABELS = {
  'program-page': 'Program Page',
  dashboard: 'Dashboard',
  bulk: 'Bulk',
  api: 'API',
  wizard: 'Wizard',
  url_import: 'URL Import',
}

// Flat, ordered row for the CSV export; exportCSV() derives columns from these keys.
// `Status` is capitalised so exportCSV won't rewrite it via the request status mapper.
export function opportunityToReportRow(o) {
  const e = o.engagement || {}
  const community =
    o.communityId && typeof o.communityId === 'object' ? o.communityId.name || '' : ''
  return {
    'Job Title': o.title || '',
    Employer: o.company || '',
    Community: community,
    Location: o.location || '',
    Type: o.jobType || '',
    Status: REPORT_STATUS_LABELS[o.status] || o.status || '',
    Source: REPORT_SOURCE_LABELS[o.source && o.source.channel] || (o.source && o.source.channel) || '',
    Submitted: o.submittedDate || '',
    Expiration: o.expirationDate || '',
    Sent: e.emailed || 0,
    Opened: e.opened || 0,
    Clicked: e.clicked || 0,
    'Not Interested': e.notInterested || 0,
    Referral: e.referral || 0,
    Applied: e.applied || 0,
    Secured: e.secured || 0,
    Feedback: e.feedback || 0,
    Unsubscribe: e.unsubscribe || 0,
  }
}

export const REPORT_DATE_FIELDS = ['Submitted', 'Expiration']

// core-api page -> { opportunities, totalCount } for vue-tables-2.
export function coreApiPageToDashboardList(page) {
  if (!page || !Array.isArray(page.data)) {
    return { opportunities: [], totalCount: 0 }
  }
  const opportunities = page.data
    .map(coreApiOpportunityToDashboard)
    .filter(Boolean)
  // core-api does not return totalCount today; falls back to data length.
  return { opportunities, totalCount: page.total || opportunities.length }
}

// dashboard create form -> core-api CreateOpportunityDto.
// Drops industry/skills/requiredExperienceYears/requiredEducation (CreateDto rejects them).
export function dashboardDraftToCoreApi(draft, communityId) {
  const lost = []
  if (draft.industry && draft.industry.length) lost.push('industry')
  if (draft.skills && draft.skills.length) lost.push('skills')
  if (draft.requiredExperienceYears != null) lost.push('requiredExperienceYears')
  if (draft.requiredEducation) lost.push('requiredEducation')
  if (lost.length) {
    // eslint-disable-next-line no-console
    console.warn('[opportunityAdapter] dropping unsupported fields:', lost.join(', '))
  }

  const payload = {
    communityId,
    title: draft.title,
    company: draft.company,
    description: draft.description,
    applicationURL: draft.applicationUrl,
    type: draft.type,
    deadline: draft.deadline,
    location: draft.location || undefined,
    salary:
      draft.salary && (draft.salary.start != null || draft.salary.end != null)
        ? {
            min: draft.salary.start,
            max: draft.salary.end,
            currency: draft.salary.currency || 'USD',
            period: draft.salary.period || 'year',
            disclosed: draft.salary.disclosed,
          }
        : undefined,
  }

  if (draft.source) {
    payload.source = {
      channel: draft.source.channel || 'dashboard',
      submitter: {
        firstName: draft.source.firstName,
        lastName: draft.source.lastName,
        email: draft.source.email,
      },
    }
  }

  return payload
}
